/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, type ReactElement } from 'react';
import { resolveTrueVaultScriptUrl } from './truevault-consent-config';

/* oxlint-disable react/no-danger -- Consent Mode must execute synchronously before deferred third-party scripts. */

const TRUEVAULT_POLARIS_LOADER_SELECTOR = 'script[data-truevault-polaris-loader]';

/**
 * RGBARRY-CUSTOMIZATION (DRAFT — W-23789028 consent rework): TrueVault Polaris CMP bootstrap.
 * TrueVault is the customer's consent management platform (it replaced OneTrust on live SFRA);
 * this loads that CMP on SF Next and bridges it to Google Consent Mode v2 so GTM/GA4 tags respect
 * the shopper's choice. See docs/TRUEVAULT-CONSENT-MODE-PLAN.md.
 *
 * ⚠️ DRAFT / scaffold — env-gated (empty `scriptUrl` renders nothing) and not enabled anywhere yet.
 *
 * Per TrueVault docs (developers.truevault.com):
 *  - The loader is `https://polaris.truevaultcdn.com/static/pc/<Privacy Center ID>/polaris.js`.
 *  - TrueVault's Developer Instructions say to load the loader FIRST, WITHOUT async/defer, ABOVE GTM.
 *    That instruction exists so TrueVault's site-SCANNER can rewrite tags before they execute. This
 *    integration deliberately does NOT use the scanner (it gates Google tags via Consent Mode and the
 *    server engagement adapters via the cookie-seeded opt-out store, see truevault-consent-store.ts), so
 *    the synchronous requirement does not apply to us. Instead (RGBARRY-CUSTOMIZATION, perf/hydration):
 *    polaris.js is preloaded early but executed ASYNC after hydration. The tiny inline Consent Mode
 *    `default` (emitted first, ahead of GTM) seeds the RETURNING shopper's opt-out synchronously from
 *    the first-party `polaris_consent_settings` cookie —
 *    so an opted-out shopper's ad signals are denied BEFORE gtm.js runs, without polaris.js blocking the
 *    render. This removes ~2.3–2.8s of mobile FCP (Lighthouse: polaris.js was the single largest
 *    render-blocking resource) and aligns with CLAUDE.md #20. The compliance guarantee moves from
 *    "loader blocks ahead of the tags" to "cookie-seeded default denies ahead of the tags"; confirm in
 *    live QA that no opted-out ad hit precedes Polaris (plan §10.3). (The URL is origin-pinned to the
 *    TrueVault CDN host in truevault-consent-config.ts, so an arbitrary-origin env value cannot load.)
 *  - Polaris does NOT call gtag() itself. Two integration modes:
 *      • RECOMMENDED (bridgeConsentMode=false): install TrueVault's official "Polaris CMP" GTM
 *        Custom Template in the container; it owns in-session consent changes. Because GTM is
 *        intentionally deferred, this component queues Polaris's first initialization event in
 *        dataLayer so the template cannot miss the current state before it exists.
 *      • MANUAL (bridgeConsentMode=true): we subscribe to Polaris's `consentChanged` window
 *        CustomEvent and translate event.detail into gtag('consent','update', …) ourselves.
 *
 * US-ONLY, ENABLED-BY-DEFAULT (customer's live config — latest findings): Baggallini operates in the
 * United States ONLY — there are NO opt-IN (GDPR/UK/CH/QC) regions to serve. Their TrueVault property
 * is therefore opt-OUT / enabled-by-default: categories are PERMITTED by default and the shopper's
 * "Do Not Sell or Share My Personal Information" choice arrives as `notOptedOut` in the
 * `consentChanged` detail (and is persisted in the `polaris_consent_settings` cookie). Two consequences,
 * both taken here:
 *   1. Consent Mode `default` is GRANTED for a NEW/undecided visitor (no cookie) — a deny-all floor is
 *      an opt-IN (GDPR) requirement this customer does not have, and granting matches "enabled by
 *      default". For a RETURNING shopper the default is seeded from their stored `polaris_consent_settings`
 *      cookie, so an opted-out shopper's ad/personalization signals are DENIED in that same first inline
 *      stub (fail-closed if the cookie is present-but-unparseable). See the defaultScript note below.
 *   2. The Polaris loader is post-hydration ASYNC (see the loader note above). It does NOT need to block: the
 *      cookie-seeded default in (1) already denies a returning opted-out shopper's ad signals BEFORE
 *      gtm.js runs, so there is no window in which their Google ad tags fire granted. Polaris then loads
 *      async to wire up the footer opt-out control and drive in-session `consentChanged` updates. NOTE:
 *      this is a US opt-out property with NO consent banner — per TrueVault, US privacy laws do not
 *      require a banner and it is an optional add-on this property does not use, so the only consent
 *      surface is the persistent footer opt-out link. TrueVault's official/current label for it is
 *      "Your Privacy Choices" (the CCPA opt-out control + icon; the same control's pre-2023 link text was
 *      "Do Not Sell or Share My Personal Information"), linking to the hosted Privacy Center opt-out page.
 *      A new visitor's granted default is the correct US opt-out posture (enabled by default until they
 *      opt out via that footer control, which then fires `consentChanged`). ⚠️ Verify in live QA
 *      (plan §10.3).
 *
 * We do NOT rely on TrueVault's site-scanner auto-blocking to stop our tags. Its scanner rewrites
 * script tags it can see in the DOM; it cannot see the Salesforce server-side engagement adapters
 * (einstein / data360 / activeData) which fire as server HTTP calls with no page script tag — so the
 * solid path is to gate on the opt-out signal explicitly (Consent Mode here for Google/GTM tags; the
 * engagement consentPreferences layer — use-analytics.ts + truevault-consent-store.ts — for the
 * server adapters; see docs/TRUEVAULT-CONSENT-MODE-PLAN.md §7).
 *
 * ENABLEMENT: the single switch is a valid https `scriptUrl` (see truevault-consent-config.ts), set
 * per MRT environment. There is NO deployment-host allowlist (which website hosts may run the CMP) —
 * a client-runtime host guard cannot be mirrored by the server-evaluated flag that disables the
 * built-in banner, and that divergence would strand a host with no consent UI at all. Distinct from
 * that: the scriptUrl's own origin IS pinned to the TrueVault CDN host in truevault-consent-config.ts
 * (so a bad env value can't load an arbitrary script) — that pin is mirrored on both sides by the
 * shared predicate, so it does not cause the strand-with-no-UI divergence.
 */

export interface TrueVaultConsentConfig {
    /** Full polaris.js URL, incl. the Privacy Center ID in the path. Empty/non-https disables the CMP. */
    scriptUrl: string;
    /** true = bridge every `consentChanged` event here. false = queue init; GTM owns later changes. */
    bridgeConsentMode: boolean;
}

/** Loads Polaris after hydration so its body mutation cannot race React. */
function TrueVaultPolarisLoader({ scriptUrl, nonce }: { scriptUrl: string; nonce?: string }): null {
    useEffect(() => {
        // Polaris initializes once per page; this also guards StrictMode effect replay.
        const loaderExists =
            document.querySelector(TRUEVAULT_POLARIS_LOADER_SELECTOR) ||
            Array.from(document.scripts).some((candidate) => candidate.src === scriptUrl);
        if (loaderExists) return;

        // RGBARRY-CUSTOMIZATION: hide Polaris's floating cookie button; footer privacy links remain available.
        // TrueVault requires polarisOptions to be set before polaris.js loads:
        // https://developers.truevault.com/docs/resources/customize-consent-manager-widgets
        const polarisWindow = window as typeof window & {
            polarisOptions?: { hideCookieButton?: boolean };
        };
        polarisWindow.polarisOptions = {
            ...polarisWindow.polarisOptions,
            hideCookieButton: true,
        };

        const script = document.createElement('script');
        script.src = scriptUrl;
        script.async = true;
        script.fetchPriority = 'low';
        script.dataset.truevaultPolarisLoader = '';
        if (nonce) script.nonce = nonce;
        document.head.appendChild(script);
    }, [scriptUrl, nonce]);

    return null;
}

export function TrueVaultConsent({
    consent,
    nonce,
}: {
    consent?: TrueVaultConsentConfig;
    nonce?: string;
}): ReactElement | null {
    // Env-gate + validation in one place (shared with config.server.ts): returns the normalized https
    // href (origin-pinned to the TrueVault CDN host) or null. The parsed href also neutralizes any
    // `</script>`-style breakout before assignment to the DOM script src property.
    const scriptUrl = resolveTrueVaultScriptUrl(consent?.scriptUrl);
    if (!scriptUrl) return null;

    const bridgeConsentMode = consent?.bridgeConsentMode ?? false;

    // Enabled-by-default state (US opt-out) — ALWAYS emitted (both modes), tiny synchronous inline stub,
    // FIRST in <head> and ahead of the GTM bootstrap. This is the piece that makes the async loader below
    // compliant: it seeds Google Consent Mode from the shopper's ALREADY-STORED choice (the first-party
    // `polaris_consent_settings` cookie), which — per TrueVault Eng (2026-08-20, plan §10 answer 2.2) —
    // arrives with the page and is readable synchronously before any app JS. So a RETURNING opted-out
    // shopper gets ad/personalization signals DENIED before gtm.js runs, WITHOUT waiting for polaris.js.
    // A brand-new / undecided visitor has no cookie → GRANTED (US enabled-by-default), the correct posture;
    // polaris.js then loads (async) to wire up the footer opt-out control ("Your Privacy Choices",
    // TrueVault's official CCPA opt-out label — this opt-out property has NO consent banner) and, when
    // the shopper toggles it in-session, fires `consentChanged` → the manual bridge / TrueVault's GTM
    // Custom Template applies the precise update.
    //
    // Cookie semantics MIRROR truevault-consent-store.ts (kept in sync deliberately — the store runs as
    // module JS, this must run as raw pre-hydration script and cannot import). The mirror has TWO halves,
    // both replicated exactly so the stub and the store never diverge on the same cookie:
    //   1. SHAPE — `isRecognizedDetail`: a non-null object in which every PRESENT known field
    //      (`notOptedOut` / `analyticsPermitted`) is a boolean AND at least one known boolean is present.
    //      A mixed shape like `{notOptedOut:true, analyticsPermitted:"yes"}` is NOT recognized — it must
    //      fail closed, not slip through as granted on the strength of the single valid field. (A loose OR
    //      here would GRANT where the store DENIES — a compliance-critical divergence.)
    //   2. DERIVATION — `deriveOptedOut`: opted-out when `notOptedOut === false` OR
    //      `analyticsPermitted === false`.
    // FAIL CLOSED on any present cookie that is unparseable OR whose shape is not recognized, matching the
    // store's CCPA "Do Not Sell or Share" posture — we cannot prove the shopper is NOT opted out, so we
    // deny in the safest direction: BOTH ad signals AND `analytics_storage` (the stub seeds
    // `__tvOut = __tvAnalyticsOff = true` before parsing). Only on a RECOGNIZED cookie does
    // `analytics_storage` track `analyticsPermitted` — granted unless analytics is explicitly denied (US
    // opt-out governs sale/sharing for ads, not first-party analytics, so a plain opt-out with
    // `analyticsPermitted:true` keeps analytics granted while denying ads). This
    // default is a strict compliance SUPERSET of the old always-granted stub: it only ever ADDS denials for
    // a returning opted-out shopper. Parity is enforced by an execution test in truevault-consent.test.tsx
    // that runs this exact stub against the same cookie fixtures as truevault-consent-store.test.ts.
    const defaultScript = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){window.dataLayer.push(arguments);}
        var __tvOut = false, __tvAnalyticsOff = false;
        try {
            var __c = document.cookie ? document.cookie.split('; ').find(function(c){return c.indexOf('polaris_consent_settings=')===0;}) : undefined;
            if (__c) {
                var __v = __c.slice('polaris_consent_settings='.length);
                if (__v) {
                    __tvOut = true; __tvAnalyticsOff = true;
                    var __d = JSON.parse(decodeURIComponent(__v));
                    if (__d !== null && typeof __d === 'object') {
                        var __noo = __d.notOptedOut, __ap = __d.analyticsPermitted;
                        var __nooOk = __noo === undefined || typeof __noo === 'boolean';
                        var __apOk = __ap === undefined || typeof __ap === 'boolean';
                        if (__nooOk && __apOk && (typeof __noo === 'boolean' || typeof __ap === 'boolean')) {
                            __tvAnalyticsOff = __ap === false;
                            __tvOut = __noo === false || __tvAnalyticsOff;
                        }
                    }
                }
            }
        } catch (e) { __tvOut = true; __tvAnalyticsOff = true; }
        var __ad = __tvOut ? 'denied' : 'granted';
        gtag('consent','default',{
            'analytics_storage': __tvAnalyticsOff ? 'denied' : 'granted',
            'ad_storage': __ad,
            'ad_user_data': __ad,
            'ad_personalization': __ad,
            'personalization_storage': __ad,
            'functionality_storage':'granted',
            'security_storage':'granted'
        });
    `;

    // RGBARRY-CUSTOMIZATION: queue Polaris's first initialization update so deferred GTM cannot miss it.
    // The official Polaris GTM template is registered only after our interaction-delayed gtm.js loads,
    // while Polaris initializes after hydration and emits `consentChanged` immediately. Capture exactly
    // that first event in dataLayer; GTM will process default then update when it loads. Remove the listener
    // before pushing so the official template remains the sole owner of later in-session changes.
    const initialConsentReplayScript = !bridgeConsentMode
        ? `
        (function(){
            function gtag(){window.dataLayer=window.dataLayer||[];window.dataLayer.push(arguments);}
            function __tvInitialConsent(event){
                window.removeEventListener('consentChanged', __tvInitialConsent);
                var d = (event && event.detail) || {};
                var g = function(v){ return v ? 'granted' : 'denied'; };
                var adsGranted = d.adsPermitted && d.notOptedOut !== false;
                gtag('consent','update',{
                    'analytics_storage': g(d.analyticsPermitted),
                    'ad_storage': g(adsGranted),
                    'ad_user_data': g(adsGranted),
                    'ad_personalization': g(adsGranted),
                    'personalization_storage': g(d.personalizationPermitted),
                    'functionality_storage': g(d.essentialPermitted),
                    'security_storage': g(d.essentialPermitted)
                });
            }
            window.addEventListener('consentChanged', __tvInitialConsent);
        })();
    `
        : '';

    // MANUAL bridge (bridgeConsentMode=true). Registered AFTER the default and BEFORE the loader, so
    // Polaris firing `consentChanged` on init lands its update on top of the established granted default
    // (in the US opt-out model the meaningful update is DENYING ad signals when the shopper opts out).
    // Mapping follows developers.truevault.com: analytics←analyticsPermitted;
    // ad_storage/ad_user_data/ad_personalization←adsPermitted (advertising; the `consentChanged` detail
    // exposes no separate targeted-advertising flag, so advertising is the documented fallback for
    // ad_personalization); personalization_storage←personalizationPermitted;
    // functionality_storage/security_storage←essentialPermitted (essential/necessary category).
    //
    // OPT-OUT SIGNAL (enabled-by-default / CCPA "Do Not Sell or Share"): the advertising signals are
    // granted only when adsPermitted AND the shopper has not opted out (`notOptedOut !== false`). In an
    // opt-out region the "Do Not Sell/Share" toggle governs the sale/sharing-for-advertising signals,
    // so opting out must deny ad_storage/ad_user_data/ad_personalization even while adsPermitted stays
    // true. `notOptedOut` is absent in pure opt-in (GDPR) regions, where `!== false` is a no-op and
    // adsPermitted alone decides — so this exact bridge is correct for BOTH the opt-out and opt-in
    // models. Analytics is intentionally NOT gated on notOptedOut (US opt-out covers sale/sharing of
    // data for advertising, not first-party analytics).
    const bridgeScript = bridgeConsentMode
        ? `
        (function(){
            function gtag(){window.dataLayer=window.dataLayer||[];window.dataLayer.push(arguments);}
            // Polaris fires 'consentChanged' on init and on every update via the footer opt-out control
            // ("Your Privacy Choices" — this opt-out property has no consent banner).
            // event.detail: { analyticsPermitted, personalizationPermitted, adsPermitted,
            //                 essentialPermitted, notOptedOut, ... }
            window.addEventListener('consentChanged', function(event){
                var d = (event && event.detail) || {};
                var g = function(v){ return v ? 'granted' : 'denied'; };
                // Advertising = permitted AND not opted out (see OPT-OUT SIGNAL note above).
                var adsGranted = d.adsPermitted && d.notOptedOut !== false;
                gtag('consent','update',{
                    'analytics_storage': g(d.analyticsPermitted),
                    'ad_storage': g(adsGranted),
                    'ad_user_data': g(adsGranted),
                    'ad_personalization': g(adsGranted),
                    'personalization_storage': g(d.personalizationPermitted),
                    'functionality_storage': g(d.essentialPermitted),
                    'security_storage': g(d.essentialPermitted)
                });
            });
        })();
    `
        : '';

    return (
        <>
            <script nonce={nonce} suppressHydrationWarning dangerouslySetInnerHTML={{ __html: defaultScript }} />
            {initialConsentReplayScript ? (
                <script
                    nonce={nonce}
                    suppressHydrationWarning
                    dangerouslySetInnerHTML={{ __html: initialConsentReplayScript }}
                />
            ) : null}
            {bridgeScript ? (
                <script nonce={nonce} suppressHydrationWarning dangerouslySetInnerHTML={{ __html: bridgeScript }} />
            ) : null}
            <link
                rel="preload"
                as="script"
                href={scriptUrl}
                fetchPriority="low"
                nonce={nonce}
                suppressHydrationWarning
            />
            {/* RGBARRY-CUSTOMIZATION: load Polaris after hydration; the inline consent default still runs before GTM. */}
            <TrueVaultPolarisLoader scriptUrl={scriptUrl} nonce={nonce} />
        </>
    );
}
